/**
 * Single source of truth for the auth token.
 *
 * Before this module existed, useAiStream read 'auth' while api.ts wrote
 * 'msk_token'. Every AI streaming endpoint silently 401'd as a result.
 *
 * Rules:
 *   - Only this module touches localStorage / sessionStorage for the token key.
 *   - All callers use getAuthToken / setAuthToken / clearAuthToken.
 *   - The canonical key is 'msk_token'. Do not change without migrating
 *     existing sessions in the same commit.
 */

import { sweepClinicalStorage } from './clinicalStorageSweep';

const TOKEN_KEY = 'msk_token';

// Legacy keys that previous versions may have written. Read-through only,
// never write. Removed from localStorage after first read so we stop
// relying on them.
const LEGACY_KEYS = ['auth', 'authToken', 'accessToken', 'token'] as const;

/**
 * Bundle 2a.1b — read a pdf_renderer JWT from `?token=` URL param,
 * but ONLY when `?print=1` is also set (defense-in-depth: URL tokens
 * only honoured for print-mode renders, never for regular user
 * navigation).
 *
 * The token is written to sessionStorage so that the Puppeteer-driven
 * tab's outbound API calls carry it via the standard Bearer header
 * path. It is NEVER written to localStorage — print-mode tabs are
 * ephemeral and the token expires in 60s anyway.
 *
 * Returns the token if consumed, null otherwise. Called once on app
 * boot from `getAuthToken()` so the SPA picks it up before any API
 * call fires.
 */
function consumePrintModeUrlToken(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const isPrintMode = params.get('print') === '1';
    const urlToken = params.get('token');
    if (!isPrintMode || !urlToken) return null;
    // Stash in sessionStorage only; the existing get-flow will pick
    // it up via the sessionStorage fallback below.
    sessionStorage.setItem(TOKEN_KEY, urlToken);
    return urlToken;
  } catch {
    return null;
  }
}

export function getAuthToken(): string | null {
  try {
    // Bundle 2a.1b — print-mode URL token takes precedence ONLY when
    // localStorage has no canonical token AND the URL carries one.
    // This is a one-shot consumption: once stashed in sessionStorage,
    // subsequent calls find it via the existing sessionStorage
    // fallback branch.
    const canonical = localStorage.getItem(TOKEN_KEY);
    if (canonical) return canonical;

    // Preserve the sessionStorage fallback pattern that existed in several
    // callers (ReportGenerator, ManagementPlan, ExerciseProgram).
    const sessionFallback = sessionStorage.getItem(TOKEN_KEY);
    if (sessionFallback) return sessionFallback;

    // Bundle 2a.1b — try URL-token ingestion before the legacy-key
    // promotion path. URL tokens are pdf_renderer-scoped and never
    // overwrite localStorage.
    const urlToken = consumePrintModeUrlToken();
    if (urlToken) return urlToken;

    // Fallback: if a legacy key still has a token from a stale session,
    // promote it to the canonical key and clean up.
    for (const legacy of LEGACY_KEYS) {
      const v = localStorage.getItem(legacy);
      if (v) {
        localStorage.setItem(TOKEN_KEY, v);
        localStorage.removeItem(legacy);
        return v;
      }
    }
    return null;
  } catch {
    return null;
  }
}

export function setAuthToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    // Clear any legacy remnants so they can't shadow the canonical key.
    for (const legacy of LEGACY_KEYS) {
      localStorage.removeItem(legacy);
    }
  } catch {
    // noop
  }
}

export function clearAuthToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    for (const legacy of LEGACY_KEYS) {
      localStorage.removeItem(legacy);
    }
  } catch {
    // noop
  }
  // Sprint B / B-6: the session is over — nothing clinical may outlive it.
  sweepClinicalStorage();
}
